import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import useDocHead from "./docs/useDocHead";

// Press kit for journalists, reviewers and marketplaces. The specs and the
// feature list are verbatim from the KVR Audio release announcement (18 Aug
// 2026), so every outlet that covers TrapMan quotes the same facts. The
// boilerplate deliberately leads on speed rather than on trap/drill/hip-hop:
// the genres are where the tool came from, not the limit of what it does.
//
// English only, deliberately: press outreach is English-language, and a
// translated press kit is one more surface to keep in sync for no benefit.
//
// Styling is borrowed from the docs (.tm-docs / .doc-prose; Docs.css is
// imported globally in App.js), so this page needs no CSS of its own.
//
// IMPORTANT, asset location: files go in frontend/public/press-kit/, NOT
// public/press/. nginx serves the SPA with `try_files $uri $uri/ /index.html`;
// a build/press/ directory with no index.html would make /press itself 403.
const PRESS_KIT_URL = "/press-kit/trapman-press-kit.zip";

// Filenames must match what scripts/build-press-kit.sh copies out of
// press-kit/screenshots/ into public/press-kit/screens/. Empty is safe: the
// section below is skipped entirely.
// One label per shot, reused three ways: the index of what each file shows, the
// figure caption, and the img alt. Filenames match press-kit/screens/.
const SCREENSHOTS = [
  { src: "/press-kit/screens/trapman-ui-blue.png", label: "the step grid and the linked piano roll, the view most of the work happens in" },
  { src: "/press-kit/screens/trapman-sample-browser.png", label: "the sample browser, with a library sorted into 12 categories on-device" },
  { src: "/press-kit/screens/trapman-sample-editor.png", label: "the sample editor: start and end points, filters, glide, pitch and an ADSR envelope" },
  { src: "/press-kit/screens/trapman-pattern-chain.png", label: "the pattern chain, where patterns become an arrangement" },
  { src: "/press-kit/screens/trapman-ui-red.png", label: "the same step grid in another of the colour themes" },
];
const fileName = (src) => src.slice(src.lastIndexOf("/") + 1);

const VIDEO_URL = "https://www.youtube.com/watch?v=EedaiyZ9E9E";
const KVR_URL = "https://www.kvraudio.com/product/trapman-by-voleor";
const PRESS_EMAIL = "support@voleor.com";
const QUOTE_ATTRIBUTION = "Sébastien Breton, founder, Voleor";

const FACTS = [
  ["Product", "TrapMan"],
  ["Developer", "Voleor"],
  ["Category", "Drum sequencer / sampler plugin"],
  ["Version", "1.0.6"],
  ["Released", "August 2026"],
  ["Price", "$59 USD, one-time perpetual license, no subscription"],
  ["Trial", "7 days, every feature unlocked, no credit card"],
  ["Licensing", "2 machine activations · works fully offline once activated"],
  ["Formats", "macOS 11+ (Apple Silicon & Intel): AU, VST3, AAX · Windows 10+: VST3, AAX"],
  ["Copy protection", "Serial number"],
  ["Included content", "TrapMan Core Kit: 808s, kicks, hi-hats, snares, claps, percussion"],
  ["Website", "voleor.com"],
];

// Verbatim from the KVR release announcement.
const FEATURES = [
  "Step grid sequencer from 1/4 down to 1/128, including triplets, with per-step velocity.",
  "Built-in piano roll, linked to the grid, for 808 lines and hat rolls.",
  "24 patterns of 1 to 16 bars, MIDI keyswitch recall from C0, and a 64-block pattern chain.",
  "Drag-and-drop sample import (WAV, AIFF, MP3, FLAC) across the lane rack.",
  "Sample editor with trim, fades, pitch, normalize and ADSR.",
  "On-device AI sample sorting that classifies kicks, snares, hats and 808s automatically.",
  "Slow for instant halftime and pitch, Slap for soft-clip knock.",
  "MIDI drag export as a keyswitched clip, plus audio and stem export as 24-bit WAV.",
  "Multi-output routing: 16 stereo outputs (Main plus Out 1-15).",
  "TrapMan Core Kit included: 808s, kicks, hi-hats, snares, claps and percussion.",
];

const KIT_CONTENTS = [
  "High-resolution screenshots (PNG, 2×)",
  "Logo set: TrapMan and Voleor, transparent PNG",
  "Product boilerplate, short and long, as plain text",
  "The full release announcement",
  "A concept render, labelled as artwork rather than a product photo",
];

export default function Press() {
  // SiteNav stays mounted across in-shell navigation, so arriving here from a
  // scrolled landing page would keep that scroll offset. DocsShell does the same.
  useEffect(() => { window.scrollTo(0, 0); }, []);

  useDocHead({
    title: "TrapMan Press Kit: Assets, Facts & Review Copies | Voleor",
    description:
      "Press kit for TrapMan, the drum sequencer plugin by Voleor: high-resolution screenshots, logos, boilerplate copy, full specifications and NFR review licenses.",
    path: "/press",
    // Deliberately unlisted: the URL is handed to journalists by email, and
    // nothing links to it. Kept out of the sitemap too (see docs-lib.mjs).
    noindex: true,
  });

  return (
    <div className="tm-docs tm-press">
      <main className="docs-main">
        <article className="doc-article">
          <header className="doc-article-head">
            <span className="docs-cat-chip">Press</span>
            <h1>TrapMan Press Kit</h1>
            <p className="docs-meta-row">
              Everything needed to cover TrapMan: assets, facts and review copies.
            </p>
          </header>

          <div className="doc-prose">
            <p>
              TrapMan is a fast drum sequencer and sampler plugin, made by{" "}
              <a href="/">Voleor</a>. Journalists, reviewers and retailers are welcome to
              use everything on this page. If something is missing, email{" "}
              <a href={`mailto:${PRESS_EMAIL}`}>{PRESS_EMAIL}</a> and it will be sent over.
            </p>

            <h2>Download the press kit</h2>
            <p>
              <a className="btn btn-primary" href={PRESS_KIT_URL}>
                Download press kit (.zip)
              </a>
            </p>
            <p>Inside:</p>
            <ul>
              {KIT_CONTENTS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>

            <h2>Review copies</h2>
            <p>
              An NFR license is available to press and reviewers on request. Email{" "}
              <a href={`mailto:${PRESS_EMAIL}`}>{PRESS_EMAIL}</a> and it will be issued the
              same day: no expiry, five machine activations. Anyone who would rather not
              wait can run the{" "}
              <a href="/download">7-day trial</a>, which unlocks every feature and needs no
              credit card.
            </p>

            <h2>Quick facts</h2>
            <table>
              <tbody>
                {FACTS.map(([label, value]) => (
                  <tr key={label}>
                    <th scope="row">{label}</th>
                    <td>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h2>Short boilerplate (50 words)</h2>
            <blockquote>
              TrapMan is a fast drum sequencer and sampler plugin. It combines lane-based
              sample playback, a step grid, a linked piano roll, a sample editor and a
              pattern arranger in a single window, so a full drum part is programmed without
              leaving the plugin. $59, AU/VST3/AAX, macOS and Windows.
            </blockquote>

            <h2>Long boilerplate (200 words)</h2>
            <blockquote>
              <p>
                Voleor's TrapMan is a drum sequencer and sampler plugin built around one
                idea: programming drums should be fast. It brings lane-based sample
                playback, a step grid, a linked piano roll, a sample editor and a pattern
                arranger together in a single window, so a full drum part can be programmed
                without leaving the plugin or fighting the DAW's piano roll.
              </p>
              <p>
                The step grid runs from 1/4 down to 1/128, including triplets, with per-step
                velocity. The built-in piano roll, linked to the grid, handles 808 lines and
                hat rolls with per-note slides. Up to 24 patterns of one to 16 bars can be
                recalled by MIDI keyswitch from C0 and chained into a 64-block arrangement.
                The sample browser classifies drums into 12 categories on-device, and two
                effects sit on the master: Slow for instant halftime and pitch, Slap for
                soft-clip knock. Parts leave as a drag-and-drop MIDI clip, or as 24-bit WAV
                stems across 16 stereo outputs. Trap and drill are where it was sharpened,
                but nothing in the grid is genre-specific: house, afrobeats, boom-bap and
                drum &amp; bass program the same way.
              </p>
              <p>
                TrapMan runs on macOS 11 and later (Apple Silicon and Intel) and Windows 10
                and later, as AU, VST3 and AAX. It costs $59 as a one-time perpetual license
                with two machine activations, works fully offline once activated, and
                includes the TrapMan Core Kit. A 7-day free trial unlocks every feature.
              </p>
            </blockquote>

            <h2>Features</h2>
            <ul>
              {FEATURES.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>

            <h2>Quote available for reuse</h2>
            <blockquote>
              <p>
                “Drum programming is where you lose the idea. TrapMan exists so that part
                takes seconds, whatever you're making.”
              </p>
              <p>{QUOTE_ATTRIBUTION}</p>
            </blockquote>

            {SCREENSHOTS.length > 0 && (
              <>
                <h2>Screenshots</h2>
                <p>
                  Each one shows a different part of the plugin, so you can pick the one that
                  fits the piece:
                </p>
                <ul>
                  {SCREENSHOTS.map((shot) => (
                    <li key={shot.src}>
                      <code>{fileName(shot.src)}</code>: {shot.label}
                    </li>
                  ))}
                </ul>
                <p>
                  Click any image for the full-resolution file, or take all of them at once
                  from the <a href={PRESS_KIT_URL}>press kit</a>.
                </p>
                {SCREENSHOTS.map((shot) => (
                  <figure key={shot.src}>
                    <a href={shot.src}>
                      <img src={shot.src} alt={`TrapMan: ${shot.label}`} width="1400" />
                    </a>
                    <figcaption>{shot.label}</figcaption>
                  </figure>
                ))}
              </>
            )}

            <h2>Links</h2>
            <ul>
              <li>
                Product page: <a href="/">voleor.com</a>
              </li>
              <li>
                Manual and feature documentation: <Link to="/docs/features">voleor.com/docs/features</Link>
              </li>
              <li>
                Video demo: <a href={VIDEO_URL} target="_blank" rel="noopener noreferrer">watch</a>
              </li>
              <li>
                KVR Audio listing: <a href={KVR_URL} target="_blank" rel="noopener noreferrer">kvraudio.com</a>
              </li>
            </ul>

            <h2>Using these assets</h2>
            <p>
              The screenshots, logos and copy on this page may be reproduced in editorial
              coverage of TrapMan, in print or online, without asking first. Please don't
              recolour, stretch or crop the logos. TrapMan and Voleor are trademarks of
              Voleor.
            </p>
          </div>
        </article>
      </main>

      <footer>
        TRAPMAN BY VOLEOR © 2026
        <div className="footer-links">
          <Link to="/docs">Docs</Link> · <a href="/privacy">Privacy</a> ·{" "}
          <a href="/">← Back to TrapMan</a>
        </div>
      </footer>
    </div>
  );
}
